/**
 * MainInfoSection 主要資料 (S1.4) — identity + units + physical, headed by the
 * ReadinessMeter (same four groups as the wizard Review step; clicking a gap
 * jumps to the owning section — the S1.4 "meter matches wizard" acceptance).
 */

import { Descriptions, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import ReadinessMeter from '@/components/common/ReadinessMeter';
import type { ReadinessMeterItem } from '@/components/common/ReadinessMeter';
import { renderDash } from '@/components/common/TableContainer';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';
import type { ProductDetailSectionKey } from '../types';

interface MainInfoSectionProps {
  product: Product;
  onEdit: () => void;
  onNavigateSection: (section: ProductDetailSectionKey) => void;
}

export const MainInfoSection = ({ product, onEdit, onNavigateSection }: MainInfoSectionProps) => {
  const { t } = useTranslation(['products', 'common']);

  const createFields = [
    product.sku,
    product.displayName,
    product.subsidiaryId,
    product.categoryId,
    product.unitsTypeId,
    product.stockUnit,
    product.purchaseUnit,
    product.saleUnit,
  ];
  const createFilled = createFields.filter((value) => Boolean(value && value.trim())).length;
  const descriptions = [
    product.salesDescription,
    product.purchaseDescription,
    product.customsDescription,
  ];
  const descriptionsFilled = descriptions.filter((value) => Boolean(value?.trim())).length;

  // Same group set + labels as the wizard Review meter (S1.3)
  const meterItems: ReadinessMeterItem[] = [
    {
      key: 'createRequired',
      label: t('products:wizard.reviewMeter.groups.createRequired'),
      detail: t('products:wizard.reviewMeter.detail', {
        filled: createFilled,
        total: createFields.length,
      }),
      complete: createFilled === createFields.length,
      onClick: () => onNavigateSection('classification'),
    },
    {
      key: 'descriptions',
      label: t('products:wizard.reviewMeter.groups.descriptions'),
      detail: t('products:wizard.reviewMeter.detail', {
        filled: descriptionsFilled,
        total: descriptions.length,
      }),
      complete: descriptionsFilled === descriptions.length,
      onClick: () => onNavigateSection('descriptions'),
    },
    {
      key: 'pricingRef',
      label: t('products:wizard.reviewMeter.groups.pricingRef'),
      complete: product.basePrice != null && product.basePrice > 0,
      onClick: () => onNavigateSection('commercial'),
    },
    {
      key: 'costingRef',
      label: t('products:wizard.reviewMeter.groups.costingRef'),
      complete: product.costingMethod != null,
      onClick: () => onNavigateSection('commercial'),
    },
  ];

  const dims =
    product.lengthMm != null && product.widthMm != null && product.heightMm != null
      ? `${product.lengthMm} × ${product.widthMm} × ${product.heightMm} mm`
      : undefined;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ReadinessMeter
        title={t('products:wizard.reviewMeter.title')}
        ready={product.readiness?.ready ?? false}
        statusText={t(
          product.readiness?.ready ? 'products:readiness.ready' : 'products:readiness.blocked'
        )}
        items={meterItems}
      />

      <div>
        <SectionHeader
          title={t('products:detail.sections.main')}
          editLabel={t('common:actions.edit')}
          onEdit={onEdit}
          editDisabled={!product.isActive}
        />
        <Descriptions column={{ xs: 1, md: 2 }} layout="vertical" bordered>
          <Descriptions.Item label={t('products:fields.sku')}>{product.sku}</Descriptions.Item>
          <Descriptions.Item label={t('products:fields.displayName')}>
            {product.displayName}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.nameZh')}>
            {renderDash(product.nameZh)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.unitsType')}>
            {product.unitsTypeId === 'UT-QTY'
              ? t('products:wizard.unitsTypes.quantity')
              : product.unitsTypeId}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:sections.units')}>
            {`${product.stockUnit} / ${product.purchaseUnit} / ${product.saleUnit}`}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.dimensions')}>
            {renderDash(dims)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.weight')}>
            {renderDash(
              product.weightKg != null
                ? `${product.weightKg} ${t(`products:weightUnits.${product.weightUnit ?? 'kg'}`)}`
                : undefined
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.cbm')}>
            {renderDash(product.cbm != null ? `${product.cbm} m³` : undefined)}
          </Descriptions.Item>
          <Descriptions.Item label={t('products:fields.cubicFeet')}>
            {renderDash(product.cubicFeet)}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Space>
  );
};

export default MainInfoSection;
